import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineTempo } from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventDependency } from '../../../slices/marketplace/timeline/models/timeline-event-dependency.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { getDayOffset } from '../../../slices/marketplace/timeline/utils/timeline-dates';

export class TimelineEventSortingService {
    static placeUpdatedEvent(
        events: TimelineEvent[],
        updatedEvent: TimelineEvent
    ): TimelineEvent[] {
        if (!events || events.length == 0) return [updatedEvent];

        const prevEventState = events.find(
            (event) => event.id == updatedEvent.id
        );
        const prevEventIndex = prevEventState
            ? events.indexOf(prevEventState)
            : -1;

        const deps = new Set(
            updatedEvent.dependencies.map((dep) => dep.dependencyEventId)
        );
        events = events.filter((event) => event.id !== updatedEvent.id);

        const firstStageOccurrence =
            events
                .map((event, index) => ({ event, index }))
                .find(({ event }) => event.stage === updatedEvent.stage)
                ?.index ?? -1;

        const eventsFromStage = events.filter(
            (event) => event.stage === updatedEvent.stage
        );
        const eventsIdsFromStage = new Set(
            eventsFromStage.map((event) => event.id)
        );
        events = events.filter((event) => !eventsIdsFromStage.has(event.id));

        const lastPositionOfDependency =
            events
                .map((event, index) => ({ event, index }))
                .reverse()
                .find(({ event }) => deps.has(event.id))?.index ?? -1;
        const positionToInsertAfterDependency =
            lastPositionOfDependency == -1 ? -1 : lastPositionOfDependency + 1;

        let positionToPlace = Math.max(
            firstStageOccurrence,
            positionToInsertAfterDependency
        );
        positionToPlace =
            positionToPlace < 0 ? prevEventIndex : positionToPlace;
        positionToPlace = positionToPlace < 0 ? events.length : positionToPlace;

        events.splice(positionToPlace, 0, ...eventsFromStage, updatedEvent);
        return events;
    }

    static sortTimelineEvents(
        events: TimelineEvent[],
        levelOfService: LevelOfService,
        tempo?: TimelineTempo,
        engagementStartDate?: Date
    ): TimelineEvent[] {
        if (!events || events.length == 0) return [];

        const stagesOrder = this.getDistinctStages(events);
        const graph = this.mapDependenciesGraph(events);
        const sortedByDependencies = this.topologicalSort(
            events,
            graph
        ).reverse();

        const groupedByStagesEvents = sortedByDependencies.reduce(
            (groups, event) => {
                if (!groups[event.stage]) {
                    groups[event.stage] = [];
                }
                groups[event.stage].push({ ...event, dayStart: 0, dayEnd: 0 });
                return groups;
            },
            {} as Record<number, TimelineEvent[]>
        );

        const groupedSorted = Object.keys(groupedByStagesEvents)
            .sort(
                (a, b) =>
                    stagesOrder.indexOf(parseInt(a)) -
                    stagesOrder.indexOf(parseInt(b))
            )
            .flatMap((stage) => groupedByStagesEvents[parseInt(stage)]);

        this.fillStartEndDay(groupedSorted, tempo, engagementStartDate);

        const timeOf = (value?: string): number | null =>
            value ? new Date(value).getTime() : null;
        const compareActual = (
            actualA: number | null,
            actualB: number | null,
            plannedA: number,
            plannedB: number
        ): number => {
            if (actualA !== null && actualB !== null) return actualA - actualB;
            if (actualA !== null) return -1;
            if (actualB !== null) return 1;
            return plannedA - plannedB;
        };

        const result = groupedSorted.sort((a, b) => {
            const stageOrderDiff = stagesOrder.indexOf(a.stage) - stagesOrder.indexOf(b.stage);
            if (stageOrderDiff !== 0) return stageOrderDiff;
            const startDiff = compareActual(
                timeOf(a.startedAt), timeOf(b.startedAt), a.dayStart, b.dayStart);
            if (startDiff !== 0) return startDiff;
            const endDiff = compareActual(
                timeOf(a.completedAt), timeOf(b.completedAt), a.dayEnd, b.dayEnd);
            if (endDiff !== 0) return endDiff;
            return a.name.localeCompare(b.name);
        });

        this.ensureDependentActionsOrderUnbroken(result);
        if (isSfas(levelOfService))
            this.ensureBuyerSelectionTeaserPostingAndExpressionOfInterestOrder(
                result
            );
        
        this.ensureMeetingEventsOrder(result);

        return result;
    }

    static ensureBuyerSelectionTeaserPostingAndExpressionOfInterestOrder(
        events: TimelineEvent[]
    ): void {
        const vvBuyerSelectionAndTeaserPostingEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        );
        const sellerTeaserEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerTeaserApproval
        );
        const buyerExpressionOfInterestEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.BuyerExpressionOfInterest
        );

        if (!!vvBuyerSelectionAndTeaserPostingEvent) {
            if (
                !sellerTeaserEvent ||
                vvBuyerSelectionAndTeaserPostingEvent.dayEnd <
                    sellerTeaserEvent.dayEnd
            ) {
                throw new Error(
                    '"Seller Teaser Approval" must exist and its end date must be before "Buyer Selection / Teaser Posting" end date'
                );
            }
        }

        if (
            !!buyerExpressionOfInterestEvent &&
            (!sellerTeaserEvent ||
                buyerExpressionOfInterestEvent.dayStart <
                    sellerTeaserEvent.dayEnd)
        ) {
            throw new Error(
                '"Seller Teaser Approval" must exist and its end date must be before "Buyer Expression Of Interest" start date'
            );
        }
    }

    static ensureMeetingEventsOrder(events: TimelineEvent[]): void {
        const provideMeetingAvailabilityEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerProvideMeetingAvailability
        );
        const agreeOnMeetingTimesEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerAgreeOnMeetingTimes
        );
        const meetingsPeriodEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetingsPeriod
        );

        // The end-date ordering is a PLANNING rule. A completed event's dayEnd is its
        // actual completion day — meeting events legitimately complete on the same day
        // or out of planned order (e.g. an admin closes the chain early), so comparisons
        // involving a completed event must not reject the timeline.
        const bothStillPlanned = (a: TimelineEvent, b: TimelineEvent) =>
            a.status !== TimelineEventStatus.Completed &&
            b.status !== TimelineEventStatus.Completed;

        // "Provide Meeting Availability" is optional and may run in parallel
        // with "Agree on Meeting Times" — when present it only bounds the
        // other meeting events from below (same-day is allowed).
        if (
            !!agreeOnMeetingTimesEvent &&
            !!provideMeetingAvailabilityEvent &&
            bothStillPlanned(
                agreeOnMeetingTimesEvent,
                provideMeetingAvailabilityEvent
            ) &&
            agreeOnMeetingTimesEvent.dayEnd <
                provideMeetingAvailabilityEvent.dayEnd
        ) {
            throw new Error(
                '"Agree on Meeting Times" end date must not be earlier than "Provide Meeting Availability" end date'
            );
        }

        if (!!meetingsPeriodEvent) {
            if (!agreeOnMeetingTimesEvent) {
                throw new Error(
                    '"Agree on Meeting Times" must exist before "Meetings Period" can be added'
                );
            }
            if (
                !!provideMeetingAvailabilityEvent &&
                bothStillPlanned(
                    meetingsPeriodEvent,
                    provideMeetingAvailabilityEvent
                ) &&
                meetingsPeriodEvent.dayEnd <
                    provideMeetingAvailabilityEvent.dayEnd
            ) {
                throw new Error(
                    '"Meetings Period" end date must not be earlier than "Provide Meeting Availability" end date'
                );
            }
            if (
                bothStillPlanned(
                    meetingsPeriodEvent,
                    agreeOnMeetingTimesEvent
                ) &&
                meetingsPeriodEvent.dayEnd < agreeOnMeetingTimesEvent.dayEnd
            ) {
                throw new Error(
                    '"Meetings Period" end date must not be earlier than "Agree on Meeting Times" end date'
                );
            }
        }
    }

    static ensureDependentActionsOrderUnbroken(events: TimelineEvent[]): void {
        const eventsWithReferencedActions = events.filter(
            (event) => event.linkedAction?.actionReferenceId
        );
        if (eventsWithReferencedActions.length === 0) return;

        const isReferencesBefore = eventsWithReferencedActions.every(
            (event) => {
                const referencedAction = event.linkedAction?.actionReferenceId;
                if (referencedAction) {
                    const referencedEvent = events.find(
                        (e) => e?.linkedAction?.id === referencedAction
                    );
                    return (
                        referencedEvent?.linkedAction.status ===
                            TimelineActionStatus.Completed ||
                        referencedEvent?.dayEnd <= event.dayStart
                    );
                }
            }
        );
        if (!isReferencesBefore) {
            const invalidEvent = eventsWithReferencedActions.find((event) => {
                const referencedAction = event.linkedAction?.actionReferenceId;
                if (referencedAction) {
                    const referencedEvent = events.find(
                        (e) => e?.linkedAction?.id === referencedAction
                    );
                    return !(
                        referencedEvent?.linkedAction.status ===
                            TimelineActionStatus.Completed ||
                        referencedEvent?.dayEnd <= event.dayStart
                    );
                }
                return false;
            });
            const referencedAction =
                invalidEvent?.linkedAction?.actionReferenceId;
            const referencedEvent = events.find(
                (e) => e?.linkedAction?.id === referencedAction
            );
            throw new Error(
                `Invalid event order: "${invalidEvent?.name}" starts before its referenced action "${referencedEvent?.name}" ends.`
            );
        }
    }

    static getDistinctStages(events: TimelineEvent[]): number[] {
        const stages = events.map((event) => event.stage);
        return stages.filter((stage, index) => stages.indexOf(stage) === index);
    }

    static recalculateCompletedEarlierEvent = (
        currentEvents: TimelineEvent[],
        completedEvent: TimelineEvent,
        engagementStartDate?: Date | string,
        simulatedNowUtc?: string
    ) => {
        const currentEventsCopy = currentEvents.slice();

        const updatedEventIndex = currentEventsCopy.findIndex(
            (f) => f.id === completedEvent.id
        );
        const updatedCompletedEvent = {
            ...completedEvent,
            completedAt:
                completedEvent.completedAt ??
                simulatedNowUtc ??
                new Date().toISOString(),
        };
        currentEventsCopy[updatedEventIndex] = updatedCompletedEvent;

        const completionOffset = (event: TimelineEvent): number =>
            event.completedAt && engagementStartDate
                ? getDayOffset(event.completedAt, engagementStartDate)
                : Number.NEGATIVE_INFINITY;

        const computeSavedTime = (parent: TimelineEvent): number => {
            let actualDurationDays: number | null = null;
            if (parent.startedAt) {
                actualDurationDays = getDayOffset(
                    parent.completedAt,
                    parent.startedAt
                );
            } else if (engagementStartDate) {
                actualDurationDays =
                    getDayOffset(parent.completedAt, engagementStartDate) -
                    parent.dayStart;
            }
            return actualDurationDays !== null
                ? Math.max(0, parent.currentTempoDurationDays - actualDurationDays)
                : 0;
        };

        const updatedRows = currentEventsCopy.map((event) => {
            const isDirectChild = (event.dependencies ?? []).some(
                (dep) => dep.dependencyEventId === updatedCompletedEvent.id
            );
            if (
                !isDirectChild ||
                event.status === TimelineEventStatus.Completed
            ) {
                return event;
            }

            const parents = (event.dependencies ?? []).map((dep) =>
                currentEventsCopy.find((ev) => ev.id === dep.dependencyEventId)
            );
            const allParentsCompleted = parents.every(
                (p) => p?.status === TimelineEventStatus.Completed
            );
            if (!allParentsCompleted) {
                return event;
            }

            // The child starts when its last-finishing (binding) parent completes, so the
            // saved days come from that parent — not necessarily the one that just completed.
            const bindingParent = parents.reduce((latest, p) =>
                completionOffset(p!) > completionOffset(latest!) ? p : latest
            )!;

            const savedTime = computeSavedTime(bindingParent);
            if (savedTime <= 0) {
                return event;
            }

            // Agree on Meeting Times and Meetings Period keep their planned end day (full saved
            // days) so the meetings window can't be compressed by an early upstream close.
            // Provide Meeting Availability stays a regular event — it only slides while it hasn't
            // started yet (once started, its start day is captured) — so it, like every
            // non-meeting event, splits the saved days in half.
            const endDayFixedActionTypes = [
                TimelineActionType.SellerBuyerAgreeOnMeetingTimes,
                TimelineActionType.SellerBuyerMeetingsPeriod,
            ];
            const keepsEndDayFixed =
                event.linkedAction?.actionType !== undefined &&
                endDayFixedActionTypes.includes(event.linkedAction.actionType);
            const extensionDays = keepsEndDayFixed
                ? savedTime
                : Math.ceil(savedTime / 2);

            return {
                ...event,
                slowTempoDurationDays: event.slowTempoDurationDays + extensionDays,
                mediumTempoDurationDays:
                    event.mediumTempoDurationDays + extensionDays,
                fastTempoDurationDays: event.fastTempoDurationDays + extensionDays,
                currentTempoDurationDays:
                    event.currentTempoDurationDays + extensionDays,
            };
        });

        return updatedRows;
    };

    private static mapDependenciesGraph(
        events: TimelineEvent[]
    ): Record<string, string[]> {
        const graph: Record<string, string[]> = {};
        events.forEach((event) => {
            if (!graph[event.id]) graph[event.id] = [];

            event.dependencies.forEach(
                (dependency: TimelineEventDependency) => {
                    if (!graph[dependency.dependencyEventId])
                        graph[dependency.dependencyEventId] = [];
                    graph[dependency.dependencyEventId].push(event.id);
                }
            );
        });

        return graph;
    }

    private static fillStartEndDay(
        events: TimelineEvent[],
        tempo?: TimelineTempo,
        engagementStartDate?: Date
    ): void {
        for (let index = 0; index < events.length; index++) {
            const ev = events[index];

            if (engagementStartDate && ev.startedAt) {
                const actualDayStart = getDayOffset(ev.startedAt, engagementStartDate);

                if (ev.completedAt) {
                    const actualDayEnd = getDayOffset(ev.completedAt, engagementStartDate);
                    events[index] = { ...ev, dayStart: actualDayStart, dayEnd: actualDayEnd };
                    continue;
                }

                const eventDuration = tempo
                    ? this.getEventDurationBasedOnTempo(ev, tempo)
                    : ev.currentTempoDurationDays;
                const plannedDayEnd = actualDayStart + eventDuration;
                events[index] = { ...ev, dayStart: actualDayStart, dayEnd: plannedDayEnd };
                continue;
            }

            let maxDepDayEnd = 0;
            if (ev.dependencies && ev.dependencies.length > 0) {
                for (const dep of ev.dependencies) {
                    const depEvent = events.find(
                        (e) => e.id === dep.dependencyEventId
                    );
                    if (
                        !depEvent ||
                        depEvent.dayStart < 0 ||
                        depEvent.dayEnd < 0
                    ) {
                        throw new Error('Events-Dependencies Order is Broken');
                    }
                    if (depEvent.dayEnd > maxDepDayEnd) maxDepDayEnd = depEvent.dayEnd;
                }
            }

            const dayStart = maxDepDayEnd;
            const eventDuration = tempo
                ? this.getEventDurationBasedOnTempo(ev, tempo)
                : ev.currentTempoDurationDays;
            const estimatedDayEnd =
                engagementStartDate && ev.completedAt
                    ? getDayOffset(ev.completedAt, engagementStartDate)
                    : dayStart + eventDuration;
            events[index] = { ...ev, dayStart, dayEnd: estimatedDayEnd };
        }
    }

    private static getEventDurationBasedOnTempo(
        event: TimelineEvent,
        tempo: TimelineTempo
    ): number {
        switch (tempo) {
            case TimelineTempo.SLOW:
                return event.slowTempoDurationDays;
            case TimelineTempo.MEDIUM:
                return event.mediumTempoDurationDays;
            case TimelineTempo.FAST:
                return event.fastTempoDurationDays;
        }
    }

    private static topologicalSort(
        events: TimelineEvent[],
        graph: Record<string, string[]>
    ): TimelineEvent[] {
        const sorted: string[] = [];
        const visited = new Set<string>();
        const visiting = new Set<string>();

        const dfs = (current: string): boolean => {
            if (visiting.has(current)) return false; // Cycle detected
            if (visited.has(current)) return true; // Already processed

            visiting.add(current);

            for (const neighbor of graph[current] || []) {
                if (!dfs(neighbor)) return false;
            }

            visiting.delete(current);
            visited.add(current);
            sorted.push(current);

            return true;
        };

        for (const event of events) {
            if (!visited.has(event.id)) {
                if (!dfs(event.id)) {
                    throw new Error(
                        'Invalid dependencies and dependent combination.'
                    );
                }
            }
        }

        return sorted.map(
            (id) => events.find((event) => event.id === id) as TimelineEvent
        );
    }

    private static ensureStatusesOrderUnbroken(events: TimelineEvent[]): void {
        for (let i = 0; i < events.length; i++) {
            const curr = events[i];

            if (curr.status !== TimelineEventStatus.Upcoming) {
                for (let j = 0; j < i; j++) {
                    const prev = events[j];

                    if (
                        prev.dayStart !== curr.dayStart &&
                        prev.status !== TimelineEventStatus.Completed
                    ) {
                        throw new Error(
                            `An uncompleted event cannot be placed before a Completed or In-Progress event.`
                        );
                    }
                }
            }
        }
    }
}
